import { HttpClient } from '@angular/common/http';
import { Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { errorTabConfig } from '@core/config/error-tab-config';
import factory from '@core/services/metrics/factory';
import { MetricsBaseService } from '@core/services/metrics/metrics-base.service';
import { PlatformService } from '@core/services/platform.service';
import { ODataService } from '@mynexus/mynexus-ui-lib';
import { DarkPopover } from '@mynexus/theme/src/scopedtokens';
import { AuthorizationCaseMetric } from '@products/dmepos/models/authorization-case-metric.model';
import { ServiceLineMetric } from '@shared/models/service-line-metric.model';
import { EvaluateData } from '@shared/models/service-line-routing.model';
import { ExpandTableConfig, TabConfig, TableConfig } from '@shared/models/table-config.model';
import { originalOrder } from '@shared/pipes/original-oder.filter';
import { LazyLoadEvent, SelectItem } from 'primeng/api';
import { Table } from 'primeng/table';
import {
  Observable,
  Subject,
  catchError,
  finalize,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { ErrorMessages } from '../routing-rules/constants';

@Component({
    selector: 'app-metrics',
    templateUrl: './metrics.component.html',
    styleUrls: ['./metrics.component.scss'],
    standalone: false
})
export class MetricsComponent implements OnInit {
  @ViewChild(Table, { static: true }) table!: Table;

  config!: ExpandTableConfig;
  columnConfig!: TableConfig;
  tabs!: TabConfig[];
  dataKey!: string;

  productName!: string | null;
  metrics$!: Observable<ServiceLineMetric[]>;
  isLoading: boolean = false;
  first = 0;
  totalRecords: number = 0;
  rowsPerPage: number = 10;
  originalOrder = originalOrder;
  routedOptions: SelectItem[] = [
    { label: 'true', value: true },
    { label: 'false', value: false },
  ];

  queueEvaluation$!: Observable<EvaluateData>;

  maxFilterDate = new Date();
  readonly maxRoutedLines = 2;

  private readonly loadQueueData$: Subject<LazyLoadEvent> = new Subject();

  private metricsService!: MetricsBaseService;

  constructor(
    private oDataService: ODataService,
    private platformService: PlatformService,
    private http: HttpClient,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.metrics$ = this.loadQueueData$.pipe(
      tap(() => (this.isLoading = true)),
      map((query) => this.metricsService.prepareQuery(query)),
      switchMap((query) => this.metricsService.query(query).pipe(catchError(() => of({ count: 0, items: [] })))),
      tap((data) => {
        this.totalRecords = data.count;
        this.isLoading = false;
      }),
      map((data) => this.mapMetrics(data.items)),
      finalize(() => (this.isLoading = false))
    );

    this.getProduct();
  }

  filterDateField(dates: [Date, Date | null] | null, field: string): void {
    let query = dates;

    if (query) {
      let [startDate, endDate] = query;
      const isRangeEqual = endDate && startDate.getMilliseconds() === endDate?.getMilliseconds();
      query = [
        startDate,
        isRangeEqual ? this.addDays(endDate as Date, 1) : endDate,
      ];
    }

    this.table.filter(query, field, 'dateRangeWihoutOffset');
  }

  private mapMetrics(data: ServiceLineMetric[]): ServiceLineMetric[] {
    const routedByProp = 'routingRequirements';

    return data.map((item: any) => {
      const record = {
        ...item,
        priorUserName: item.priorUserName || (item.isRouted ? 'New' : item.priorUserName),
      };

      if (!item[routedByProp]) {
        return record;
      }

      return {
        ...record,
        routedBy: record[routedByProp],
        routedByLength: Object.keys(record[routedByProp]).length - this.maxRoutedLines,
        tooltip: this.generateTooltipHTML(record[routedByProp]),
      };
    });
  }

  private getProduct(): void {
    this.platformService.currentProduct
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (product) => {
        this.metricsService = factory(this.platformService, this.http, this.oDataService);
        this.productName = product;
        this.dataKey = this.metricsService.uniqueKey;
        this.config = await this.platformService.getMetricsConfig();
        this.columnConfig = this.config.table;
        this.tabs = this.config.expandedView;
      });
  }

  public fetchTableData(event: LazyLoadEvent = this.table.createLazyLoadMetadata()): void {
    this.collapseExpandedRow();
    this.loadQueueData$.next(event);
  }

  getExpandedData(metric: ServiceLineMetric | AuthorizationCaseMetric): void {
    this.tabs = this.config.expandedView;

    this.queueEvaluation$ = this.metricsService
      .getEvaluateQueue(metric)
      .pipe(
        catchError(() => {
          this.tabs = errorTabConfig;

          return [{ error: ErrorMessages.GENERAL }];
        })
      );
  }

  collapseExpandedRow(): void {
    this.table.expandedRowKeys = {};
  }

  private addDays(date: Date, days: number) {
    let result = new Date(date);
    result.setDate(result.getDate() + days);

    return result;
  }

  private generateTooltipHTML(obj: object): string {
    if (!obj) {
      return '';
    }

    const list = document.createElement('ul');

    Object.keys(obj).forEach((key: any) => {
      const listItem = document.createElement('li');
      listItem.innerHTML = `${key}: <span class="font-semibold">${(obj as any)[key]}</span>`;
      list.appendChild(listItem);
    });

    return list.outerHTML;
  }

  public clearFilters(): void {
    const { sortOrder, sortField, filters, first } = this.getDefaultFiltersState();

    this.table.lazy = false;
    this.table.first = first;
    this.table.filters = filters;
    (this.table as any).createdDate = null;
    this.table.sortOrder = sortOrder;
    this.table.sortField = sortField;
    this.table.totalRecords = this.totalRecords;
    this.table.saveState();
    this.table.lazy = true;
    this.table.sortSingle();
  }

  private getDefaultFiltersState() {
    return {
      filters: {},
      first: 0,
      globalFilter: null,
      sortField: 'createdDate',
      sortOrder: -1,
    };
  }

  protected readonly DarkPopover = DarkPopover;
}
